<script setup lang="ts">
import { RouteName } from '@/router/models/route-name';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import { projectService } from '@/services/project/project-service.ts';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { hasPermission } from 'cesg-shared';
import { PermissionResource, PermissionType } from 'cesg-shared/dist/models';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();

defineProps({
  projectId: String,
});

const route = useRoute();
const user = entraAuthService.me.value;

const isActive = (tabs: RouteName[]) => {
  if (!route.name) return false;
  return tabs.some((tab) => route.name === tab);
};

const hasOverviewPermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.OVERVIEW,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});
const hasBudgetPermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.BUDGET,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});
const hasForecastPermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.FORECAST,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});
const hasATAPermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.ATA,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});
const hasInvoicePermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.INVOICE,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});
const hasFilesPermission = computed(() => {
  if (user) {
    return hasPermission(
      route.params.id.toString(),
      [
        {
          resource: PermissionResource.FILES,
          permission: PermissionType.VIEW,
        },
      ],
      user!,
    );
  }
  return false;
});

const projectName = ref<string>('');

onMounted(async () => {
  const project = await projectService.getProjectname(
    route.params.id as string,
  );
  projectName.value = toProjectDisplayName(project);
});
</script>

<template>
  <div class="tabs">
    <ul>
      <li
        :class="{ 'is-active': isActive([RouteName.Overview]) }"
        v-if="hasOverviewPermission"
      >
        <router-link
          :to="{ name: RouteName.Overview, params: { id: projectId } }"
          >{{ t('navigation.menu.overview') }}</router-link
        >
      </li>
      <li
        :class="{ 'is-active': isActive([RouteName.CalculationBudget]) }"
        v-if="hasBudgetPermission"
      >
        <router-link
          :to="{ name: RouteName.CalculationBudget, params: { id: projectId } }"
          >{{ t('navigation.menu.calculationAndBudget') }}</router-link
        >
      </li>
      <li
        :class="{ 'is-active': isActive([RouteName.Forecast]) }"
        v-if="hasForecastPermission"
      >
        <router-link
          :to="{ name: RouteName.Forecast, params: { id: projectId } }"
          >{{ t('navigation.menu.forecast') }}</router-link
        >
      </li>
      <li
        :class="{
          'is-active': isActive([RouteName.ATA, RouteName.ATADetails]),
        }"
        v-if="hasATAPermission"
      >
        <router-link :to="{ name: RouteName.ATA, params: { id: projectId } }">{{
          t('navigation.menu.ata')
        }}</router-link>
      </li>
      <li
        :class="{
          'is-active': isActive([RouteName.Invoices, RouteName.InvoiceDetails]),
        }"
        v-if="hasInvoicePermission"
      >
        <router-link
          :to="{
            name: RouteName.Invoices,
            params: { id: projectId },
          }"
          >{{ t('navigation.menu.invoices') }}</router-link
        >
      </li>
      <li
        :class="{ 'is-active': isActive([RouteName.Documents]) }"
        v-if="hasFilesPermission"
      >
        <router-link
          :to="{ name: RouteName.Documents, params: { id: projectId } }"
          >{{ t('navigation.menu.documents') }}</router-link
        >
      </li>
    </ul>
    <div class="is-float-right is-size-4" data-test="project-name">
      {{ projectName }}
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/style/variables';
.tabs ul {
  border-bottom: none;
  font-weight: 500;
  font-size: 1rem;

  li:first-child a {
    padding-left: 0;
  }

  .is-active a {
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
  }

  :not(.is-active) a {
    border-bottom: none;
    color: var(--inactive-color);
  }
}
</style>
